import {
  hideFloatingTooltip,
  hideFloatingTooltipForAnchor,
  isPointerOverFloatingTooltip,
  showFloatingTooltipHtml,
} from "./floating_tooltip";

const COLLAPSED = "collapsed";
const TOOLTIP_TARGET_SELECTOR = "[data-workflow-step-nav-row]";

const lastPointer = { x: null, y: null };
let activeTooltipTarget = null;
let initialized = false;

function rememberPointer(event) {
  lastPointer.x = event.clientX;
  lastPointer.y = event.clientY;
}

function navPanelElement() {
  return document.querySelector(".workflow-step-shell__nav .step-nav-desktop");
}

function isWorkflowStepNavCollapsed() {
  const layout =
    document.documentElement.dataset.workflowStepNavLayout ||
    document.querySelector(".workflow-step-shell")?.dataset.workflowStepNavLayout ||
    window.__DEPOZEN_WORKFLOW_STEP_NAV_LAYOUT__;

  return layout === COLLAPSED;
}

function tooltipTargetFrom(node) {
  const panel = navPanelElement();
  if (!(node instanceof Element) || !panel) return null;

  const target = node.closest(TOOLTIP_TARGET_SELECTOR);
  return target && panel.contains(target) ? target : null;
}

function findTooltipTargetAtPoint(clientX, clientY) {
  if (clientX == null || clientY == null) return null;

  const panel = navPanelElement();
  if (!panel) return null;

  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof Element) || !panel.contains(element)) continue;

    const target = tooltipTargetFrom(element);
    if (target) return target;
  }

  return null;
}

function showCollapsedTooltip(target) {
  if (!target?.isConnected) return;

  const source = target.querySelector(
    ".workflow-step-nav-row__tooltip-source .workflow-step-nav-floating-tooltip",
  );
  if (!source) return;

  showFloatingTooltipHtml(target, source.innerHTML, { placement: "right", interactive: true });

  const tooltip = document.querySelector(".chart-segment-tooltip.is-visible");
  if (!tooltip || tooltip.dataset.workflowStepNavTooltipBound === "true") return;

  tooltip.dataset.workflowStepNavTooltipBound = "true";
  tooltip.addEventListener("pointerenter", handleFloatingTooltipPointerEnter);
  tooltip.addEventListener("pointerleave", handleFloatingTooltipPointerLeave);
}

function clearCollapsedTooltip() {
  activeTooltipTarget = null;
  hideFloatingTooltip({ force: true });
  document.querySelectorAll(".chart-segment-tooltip[data-workflow-step-nav-tooltip-bound]").forEach((node) => {
    node.removeAttribute("data-workflow-step-nav-tooltip-bound");
  });
}

function setActiveTooltipTarget(target) {
  if (activeTooltipTarget && !activeTooltipTarget.isConnected) {
    activeTooltipTarget = null;
  }

  if (target && !target.isConnected) target = null;

  if (target === activeTooltipTarget) {
    if (target) showCollapsedTooltip(target);
    return;
  }

  if (activeTooltipTarget) hideFloatingTooltipForAnchor(activeTooltipTarget);

  activeTooltipTarget = target;

  if (target) {
    showCollapsedTooltip(target);
  } else {
    hideFloatingTooltip({ force: true });
  }
}

function syncTooltipFromPointer(clientX, clientY) {
  if (!isWorkflowStepNavCollapsed()) {
    clearCollapsedTooltip();
    return;
  }

  setActiveTooltipTarget(findTooltipTargetAtPoint(clientX, clientY));
}

function refreshTooltipUnderPointer() {
  if (!isWorkflowStepNavCollapsed()) {
    clearCollapsedTooltip();
    return;
  }

  syncTooltipFromPointer(lastPointer.x, lastPointer.y);
}

function scheduleRefresh() {
  const run = () => refreshTooltipUnderPointer();

  requestAnimationFrame(() => {
    run();
    requestAnimationFrame(run);
  });
  window.setTimeout(run, 0);
}

function handlePointerMove(event) {
  rememberPointer(event);

  if (!isWorkflowStepNavCollapsed()) {
    if (activeTooltipTarget) clearCollapsedTooltip();
    return;
  }

  const panel = navPanelElement();
  if (!panel) return;

  const target = findTooltipTargetAtPoint(event.clientX, event.clientY);
  if (target) {
    setActiveTooltipTarget(target);
  } else if (isPointerOverFloatingTooltip(event.clientX, event.clientY)) {
    // Laisser le curseur atteindre le lien edit dans le tooltip flottant.
  } else if (activeTooltipTarget) {
    clearCollapsedTooltip();
  }
}

function handleFloatingTooltipPointerEnter() {
  if (!isWorkflowStepNavCollapsed() || !activeTooltipTarget) return;
  showCollapsedTooltip(activeTooltipTarget);
}

function handleFloatingTooltipPointerLeave(event) {
  if (!isWorkflowStepNavCollapsed() || !activeTooltipTarget) return;

  const related = event.relatedTarget;
  if (related instanceof Node && activeTooltipTarget.contains(related)) return;
  if (related instanceof Element && related.closest(".chart-segment-tooltip")) return;

  window.setTimeout(() => {
    if (!isWorkflowStepNavCollapsed() || !activeTooltipTarget) return;
    if (isPointerOverFloatingTooltip(lastPointer.x, lastPointer.y)) return;
    if (findTooltipTargetAtPoint(lastPointer.x, lastPointer.y)) return;
    clearCollapsedTooltip();
  }, 40);
}

function handlePointerOver(event) {
  if (!isWorkflowStepNavCollapsed()) return;

  const panel = navPanelElement();
  if (!(event.target instanceof Node) || !panel?.contains(event.target)) return;

  rememberPointer(event);
  syncTooltipFromPointer(event.clientX, event.clientY);
}

function handleFocusIn(event) {
  if (!isWorkflowStepNavCollapsed()) return;

  const target = tooltipTargetFrom(event.target);
  if (!target) return;

  const focusable = target.matches("a, button, [tabindex]")
    ? target
    : target.querySelector("a, button, [tabindex]");
  if (focusable && !focusable.matches(":focus-visible")) return;

  setActiveTooltipTarget(target);
}

function handleFocusOut(event) {
  if (!isWorkflowStepNavCollapsed()) return;

  const target = tooltipTargetFrom(event.target);
  if (!target || activeTooltipTarget !== target) return;

  const related = event.relatedTarget;
  if (related instanceof Node && target.contains(related)) return;

  clearCollapsedTooltip();
}

function handleScroll(event) {
  if (!activeTooltipTarget?.isConnected || !isWorkflowStepNavCollapsed()) return;
  if (event.target?.closest?.(".workflow-step-shell__nav")) return;

  showCollapsedTooltip(activeTooltipTarget);
}

export function initializeWorkflowStepNavCollapsedTooltips() {
  if (initialized) return;
  initialized = true;

  document.addEventListener("pointermove", handlePointerMove, { passive: true });
  document.addEventListener("pointerover", handlePointerOver);
  document.addEventListener("pointerdown", rememberPointer, { passive: true });
  document.addEventListener("focusin", handleFocusIn);
  document.addEventListener("focusout", handleFocusOut);
  document.addEventListener("scroll", handleScroll, true);

  document.addEventListener("turbo:before-cache", clearCollapsedTooltip);
  document.addEventListener("turbo:render", scheduleRefresh);
  document.addEventListener("turbo:load", scheduleRefresh);

  document.addEventListener("depozen:workflow-step-nav-layout-change", scheduleRefresh);
}

export function refreshWorkflowStepNavCollapsedTooltips() {
  scheduleRefresh();
}

export function clearWorkflowStepNavCollapsedTooltips() {
  clearCollapsedTooltip();
}
